import React, { useRef, useState } from "react";
import { motion } from "framer-motion";
import { Star, Play, Video, Image as ImageIcon, Users, Volume2, VolumeX, MapPin, MessageCircle } from "lucide-react";
import { fileUrl, videoUrl, posterUrl, videoProcessing, displayTitle } from "@/lib/api";
import { VideoProcessing } from "@/components/VideoProcessing";
import { useVideoPoll } from "@/hooks/useVideoPoll";
import { computeFraming } from "@/lib/coverFraming";

// THE gallery media card. Used by every gallery surface (collections incl. No Location / Smart / system,
// tag galleries, Browse, profiles, leaderboards, AI search). One portrait 3:4 shell for photos AND videos;
// the source keeps its own aspect and is object-fit: cover'd with a FEET-FIRST focal point.
export const CARD_ASPECT = 3 / 4;
const FEET_BIAS = { x: 50, y: 68 };   // fallback when no subject analysis exists: sit low so legs/feet survive the crop

export const cardFocal = (item) => {
  if (item?.frame?.raw) {
    const f = computeFraming(item.frame.raw, CARD_ASPECT);
    if (f.source !== "default") return { x: f.x, y: f.y, zoom: f.zoom || 1, source: f.source };   // subject analysis or admin override
  }
  return { ...FEET_BIAS, zoom: 1, source: "feet-bias-default" };
};

export const MediaCard = ({ item: initialItem, index = 0, onOpen, testid }) => {
  const item = useVideoPoll(initialItem);
  const videoRef = useRef(null);
  const [loaded, setLoaded] = useState(false);
  const [sound, setSound] = useState(false); // false = muted (default), true = playing original audio
  const isVideo = item.media_type === "video";
  // Slow-mo clips are re-rendered silent (audio dropped), so they never have a usable track.
  // Otherwise assume a video has audio until the element positively tells us it doesn't.
  const [hasAudio, setHasAudio] = useState(isVideo && !item.video_slowmo);

  const detectAudio = (v) => {
    if (!v) return;
    // Only trust a *negative* signal from the APIs that support it; leave the default otherwise.
    if (v.mozHasAudio === false) return setHasAudio(false);
    if (v.audioTracks && typeof v.audioTracks.length === "number" && v.audioTracks.length === 0) return setHasAudio(false);
  };

  const toggleSound = (e) => {
    e.stopPropagation();
    const v = videoRef.current;
    if (!v) return;
    const next = !sound;
    v.muted = !next;
    if (next) {
      // Only show the "unmuted" state if playback actually starts (autoplay policies can block it).
      Promise.resolve(v.play()).then(() => setSound(true)).catch(() => { v.muted = true; setSound(false); });
    } else {
      setSound(false);
    }
  };

  const handleEnter = () => { if (isVideo && videoRef.current) videoRef.current.play().catch(() => {}); };
  const handleLeave = () => {
    if (isVideo && videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
  };

  const focal = cardFocal(item);
  const focalStyle = { objectPosition: `${focal.x}% ${focal.y}%`, ...(focal.zoom > 1 ? { transform: `scale(${focal.zoom})`, transformOrigin: `${focal.x}% ${focal.y}%` } : {}) };

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: Math.min(index * 0.05, 0.6), ease: "easeOut" }}
      data-testid={testid || `media-card-${item.id}`}
      data-card-aspect="3:4"
      data-card-focal={`${focal.x},${focal.y}`}
      data-card-focal-source={focal.source}
      onClick={() => onOpen(item)}
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
      className="group relative isolate overflow-hidden rounded-lg bg-card border border-white/5 cursor-pointer hover:border-primary/40 transition-colors duration-300"
    >
      <div className="relative overflow-hidden aspect-[3/4] bg-black">
        {isVideo ? (
          videoProcessing(item) ? (
            <VideoProcessing failed={item.video_failed} stalled={item.processing_state === "stalled"} />
          ) : (
          <video
            ref={videoRef}
            src={videoUrl(item)}
            poster={posterUrl(item, 800)}
            muted={!sound}
            loop
            playsInline
            preload="metadata"
            onLoadedData={() => setLoaded(true)}
            onLoadedMetadata={(e) => detectAudio(e.currentTarget)}
            style={focalStyle}
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
          />
          )
        ) : (
          <img
            src={`${fileUrl(item.file_ref)}?w=600`}
            alt={item.title}
            loading="lazy"
            onLoad={() => setLoaded(true)}
            style={focalStyle}
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
          />
        )}

        {/* Top scrim keeps the always-on title/badges readable without covering the subject */}
        <div className="pointer-events-none absolute inset-x-0 top-0 z-10 bg-gradient-to-b from-black/80 via-black/35 to-transparent p-2.5 pb-8">
          <div className="flex items-start justify-between gap-2">
            {/* media type — clear PHOTO / VIDEO badge */}
            <span
              title={isVideo ? "Video" : "Photo"}
              className="flex items-center gap-1 text-[9px] tracking-[0.15em] uppercase font-bold px-1.5 py-0.5 rounded bg-black/55 backdrop-blur-md border border-white/10 text-white/90"
            >
              {isVideo ? <Video className="w-2.5 h-2.5" /> : <ImageIcon className="w-2.5 h-2.5" />}
              {isVideo ? "Video" : "Photo"}
            </span>

            {/* rating — top right */}
            {item.rating_count > 0 && (
              <span className="flex items-center gap-1 px-1.5 py-0.5 rounded bg-black/55 backdrop-blur-md border border-white/10">
                <Star className="w-3 h-3 fill-primary text-primary" />
                <span className="text-xs font-semibold font-mono text-white">{item.avg_rating}</span>
              </span>
            )}
          </div>

          {/* title — always visible, truncates gracefully */}
          <h3 data-testid={`card-title-${item.id}`} className="mt-1.5 font-heading font-medium text-white text-sm leading-tight truncate drop-shadow">
            {displayTitle(item.title)}
          </h3>
        </div>

        {/* community source chip (understated, below the title block) */}
        {!item.is_official && (
          <span data-testid="community-badge" className="absolute top-[52px] left-2.5 z-10 flex items-center gap-1 text-[9px] tracking-[0.15em] uppercase font-bold px-1.5 py-0.5 rounded bg-emerald-500/85 text-white">
            <Users className="w-2.5 h-2.5" /> Community
          </span>
        )}

        {isVideo && !videoProcessing(item) && (
          <div className="absolute inset-0 flex items-center justify-center opacity-100 group-hover:opacity-0 transition-opacity pointer-events-none">
            <div className="w-14 h-14 rounded-full bg-black/50 backdrop-blur-md border border-white/20 flex items-center justify-center">
              <Play className="w-6 h-6 text-white fill-white ml-0.5" />
            </div>
          </div>
        )}

        {/* video audio — only on videos that actually have a track; listen to the original audio */}
        {isVideo && hasAudio && !videoProcessing(item) && (
          <button
            data-testid={`sound-${item.id}`}
            onClick={toggleSound}
            title={sound ? "Mute" : "Turn sound on"}
            aria-label={sound ? "Mute" : "Turn sound on"}
            className={"absolute bottom-3 left-3 z-20 w-9 h-9 rounded-full backdrop-blur-md border flex items-center justify-center transition-colors " + (sound ? "bg-primary text-primary-foreground border-primary" : "bg-black/60 border-white/15 text-white hover:bg-primary hover:text-primary-foreground")}
          >
            {sound ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
          </button>
        )}

        {/* bottom overlay — creator only (no repeated title), revealed on hover */}
        <div className="absolute inset-x-0 bottom-0 z-10 p-3 pt-10 bg-gradient-to-t from-black/85 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
          <div className={"flex items-center gap-2 text-xs text-white/70 min-w-0 " + (isVideo && hasAudio ? "pl-11" : "")}>
            <span className="truncate">by {item.uploader_name}</span>
            <span className="shrink-0">· {item.rating_count || 0} ratings</span>
            {item.comment_count > 0 && <span className="flex items-center gap-1 shrink-0"><MessageCircle className="w-3 h-3" /> {item.comment_count}</span>}
          </div>
          {item.location?.display && (
            <div className="mt-0.5 flex items-center gap-1 text-[11px] text-white/80 truncate" title={item.location.display} data-testid={`tile-loc-${item.id}`}><MapPin className="w-3 h-3 text-primary shrink-0" /> <span className="truncate">{item.location.city || item.location.display}</span></div>
          )}
        </div>
      </div>
    </motion.div>
  );
};
